// Rate Limiting Utility for Frontend
// Prevents spam and abuse on client side

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  blockDurationMs?: number;
}

interface RateLimitEntry {
  attempts: number;
  firstAttempt: number;
  blockedUntil?: number;
}

class RateLimiter {
  private storage = new Map<string, RateLimitEntry>();
  private configs = new Map<string, RateLimitConfig>();

  // Configure rate limits for different actions
  configure(action: string, config: RateLimitConfig) {
    this.configs.set(action, config);
  }

  // Check if action is allowed
  isAllowed(action: string, identifier: string = 'default'): boolean {
    const config = this.configs.get(action);
    if (!config) return true; // No limit configured

    const key = `${action}:${identifier}`;
    const now = Date.now();
    const entry = this.storage.get(key);

    // Clean up expired entries
    this.cleanup();

    // Check if currently blocked
    if (entry?.blockedUntil && now < entry.blockedUntil) {
      return false;
    }

    // No previous attempts or window expired
    if (!entry || now - entry.firstAttempt > config.windowMs) {
      this.storage.set(key, {
        attempts: 1,
        firstAttempt: now
      });
      return true;
    }

    // Within window, check attempts
    if (entry.attempts >= config.maxAttempts) {
      // Block for specified duration
      const blockDuration = config.blockDurationMs || config.windowMs;
      this.storage.set(key, {
        ...entry,
        blockedUntil: now + blockDuration
      });
      return false;
    }

    // Increment attempts
    entry.attempts++;
    this.storage.set(key, entry);
    return true;
  }

  // Get remaining attempts
  getRemainingAttempts(action: string, identifier: string = 'default'): number {
    const config = this.configs.get(action);
    if (!config) return Infinity;

    const key = `${action}:${identifier}`;
    const entry = this.storage.get(key);
    
    if (!entry) return config.maxAttempts;
    
    const now = Date.now();
    if (now - entry.firstAttempt > config.windowMs) {
      return config.maxAttempts;
    }

    return Math.max(0, config.maxAttempts - entry.attempts);
  }

  // Get time until unblocked
  getBlockedUntil(action: string, identifier: string = 'default'): number | null {
    const key = `${action}:${identifier}`;
    const entry = this.storage.get(key);
    
    if (!entry?.blockedUntil) return null;
    
    const now = Date.now();
    return entry.blockedUntil > now ? entry.blockedUntil : null;
  }

  // Reset rate limit for action/identifier
  reset(action: string, identifier: string = 'default') {
    const key = `${action}:${identifier}`;
    this.storage.delete(key);
  }

  // Clean up expired entries
  private cleanup() {
    const now = Date.now();
    for (const [key, entry] of this.storage.entries()) {
      const config = this.configs.get(key.split(':')[0]);
      if (!config) continue;

      // Remove if window expired and not blocked
      if (!entry.blockedUntil && now - entry.firstAttempt > config.windowMs) {
        this.storage.delete(key);
      }
      // Remove if block expired
      else if (entry.blockedUntil && now > entry.blockedUntil) {
        this.storage.delete(key);
      }
    }
  }
}

// Global rate limiter instance
export const rateLimiter = new RateLimiter();

// Configure common rate limits
rateLimiter.configure('login', {
  maxAttempts: 5,
  windowMs: 15 * 60 * 1000, // 15 minutes
  blockDurationMs: 30 * 60 * 1000 // 30 minutes block
});

rateLimiter.configure('signup', {
  maxAttempts: 3,
  windowMs: 60 * 60 * 1000, // 1 hour
  blockDurationMs: 2 * 60 * 60 * 1000 // 2 hours block
});

rateLimiter.configure('password-reset', {
  maxAttempts: 3,
  windowMs: 60 * 60 * 1000, // 1 hour
  blockDurationMs: 60 * 60 * 1000 // 1 hour block
});

rateLimiter.configure('job-creation', {
  maxAttempts: 10,
  windowMs: 60 * 60 * 1000, // 1 hour
  blockDurationMs: 30 * 60 * 1000 // 30 minutes block
});

rateLimiter.configure('form-submission', {
  maxAttempts: 20,
  windowMs: 60 * 1000, // 1 minute
  blockDurationMs: 5 * 60 * 1000 // 5 minutes block
});

// Helper function to get user identifier
export function getUserIdentifier(): string {
  // Use IP simulation or session ID
  return sessionStorage.getItem('session-id') || 
         localStorage.getItem('user-id') || 
         'anonymous';
}

// Rate limit decorator for functions
export function rateLimit(action: string) {
  return function (_target: any, _propertyName: string, descriptor: PropertyDescriptor) {
    const method = descriptor.value;

    descriptor.value = function (...args: any[]) {
      const identifier = getUserIdentifier();
      
      if (!rateLimiter.isAllowed(action, identifier)) {
        const blockedUntil = rateLimiter.getBlockedUntil(action, identifier);
        const remaining = rateLimiter.getRemainingAttempts(action, identifier);
        
        throw new Error(
          `Rate limit exceeded for ${action}. ` +
          `Remaining attempts: ${remaining}. ` +
          (blockedUntil ? `Blocked until: ${new Date(blockedUntil).toLocaleTimeString()}` : '')
        );
      }

      return method.apply(this, args);
    };
  };
}